"use client";

import { ShareIcon } from "@money/frontend/components/icons/share.icon.tsx";
import { ExternalLinkIcon } from "@money/frontend/components/icons/external-link.icon.tsx";
import { CheckIcon } from "@money/frontend/components/icons/check.icon.tsx";
import { InfoIcon } from "@money/frontend/components/icons/info.icon.tsx";
import { Dropdown } from "@money/frontend/components/ui/dropdown.tsx";
import { Button } from "@money/frontend/components/ui/button.tsx";
import { Tooltip } from "@money/frontend/components/ui/tooltip.tsx";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "next/navigation";
import dayjs from "dayjs";
import Link from "next/link";

interface CompanyData {
  id: string;
  name: string;
  description: string;
  logoUrl: string;
  websiteUrl: string;
  totalRevenue: number;
  createdAt: string;
  lastDateSynced: string | null;
  provider: string;
  organization: {
    users: Array<{
      user: {
        xHandle: string | null;
      };
    }>;
  };
  revenues: Array<{
    currency: string;
    amount: number;
    date: string;
    type: "REVENUE" | "MRR";
  }>;
}

function formatCurrency(amount: number, currency: string = "USD"): string {
  const majorUnits = amount / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(majorUnits);
}

function formatDate(date: string | Date): string {
  return dayjs(date).format("MMMM YYYY");
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

export default function CompanyDetailPage() {
  const params = useParams();
  const slug = params.id as string;
  const [timeFilter, setTimeFilter] = useState("All time");
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    async function fetchCompanyData() {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
        if (!backendUrl) {
          throw new Error("NEXT_PUBLIC_BACKEND_URL is not set");
        }

        const response = await fetch(`${backendUrl}/billing/${slug}`);
        if (!response.ok) {
          throw new Error("Company not found");
        }

        const data = await response.json();
        setCompanyData(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load company data"
        );
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      fetchCompanyData();
    }
  }, [slug]);

  // Share functionality
  const handleShare = useCallback(async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-12">
          {/* Company Profile Skeleton */}
          <div className="flex flex-col lg:flex-row items-start justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex items-start gap-4 sm:gap-6 w-full lg:w-auto">
              {/* Company Logo Skeleton */}
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />

              {/* Company Info Skeleton */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                  <div className="h-8 sm:h-9 w-40 sm:w-64 bg-gray-200 rounded animate-pulse" />
                  <div className="h-6 sm:h-7 w-20 sm:w-24 bg-gray-200 rounded-lg animate-pulse" />
                </div>
                <div className="h-4 sm:h-5 w-full max-w-2xl bg-gray-200 rounded animate-pulse mb-2" />
                <div className="h-3 sm:h-4 w-32 sm:w-40 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>

            {/* Action Buttons Skeleton */}
            <div className="flex items-center gap-2 sm:gap-3 w-full lg:w-auto">
              <div className="h-9 sm:h-10 w-full sm:w-24 bg-gray-200 rounded-lg animate-pulse flex-1 sm:flex-initial" />
              <div className="h-9 sm:h-10 w-full sm:w-20 bg-gray-200 rounded-lg animate-pulse flex-1 sm:flex-initial" />
            </div>
          </div>

          {/* Metrics Cards Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 sm:mb-8">
            {/* Total Revenue Skeleton */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <div className="h-3 sm:h-4 w-24 sm:w-32 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-8 sm:h-9 w-32 sm:w-40 bg-gray-200 rounded animate-pulse mb-1" />
              <div className="h-3 sm:h-4 w-16 sm:w-20 bg-gray-200 rounded animate-pulse" />
            </div>

            {/* Last 30 Days Skeleton */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <div className="h-3 sm:h-4 w-24 sm:w-32 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-8 sm:h-9 w-32 sm:w-40 bg-gray-200 rounded animate-pulse mb-1" />
              <div className="h-3 sm:h-4 w-20 sm:w-24 bg-gray-200 rounded animate-pulse" />
            </div>

            {/* MRR Skeleton */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <div className="h-3 sm:h-4 w-28 sm:w-36 bg-gray-200 rounded animate-pulse mb-2 flex items-center gap-1">
                <div className="h-3 w-3 bg-gray-300 rounded-full animate-pulse" />
              </div>
              <div className="h-8 sm:h-9 w-24 sm:w-32 bg-gray-200 rounded animate-pulse mb-1" />
              <div className="h-3 sm:h-4 w-20 sm:w-24 bg-gray-200 rounded animate-pulse" />
            </div>

            {/* ARR Skeleton */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <div className="h-3 sm:h-4 w-28 sm:w-36 bg-gray-200 rounded animate-pulse mb-2 flex items-center gap-1">
                <div className="h-3 w-3 bg-gray-300 rounded-full animate-pulse" />
              </div>
              <div className="h-8 sm:h-9 w-24 sm:w-32 bg-gray-200 rounded animate-pulse mb-1" />
              <div className="h-3 sm:h-4 w-20 sm:w-24 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>

          {/* Revenue Graph Section Skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8 overflow-x-auto">
            {/* Filters Skeleton */}
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="h-9 sm:h-10 w-28 sm:w-32 bg-gray-200 rounded-lg animate-pulse" />
            </div>

            {/* Chart Skeleton */}
            <div className="relative h-64 sm:h-80 md:h-96 min-w-[600px] sm:min-w-0">
              {/* Y-axis labels skeleton */}
              <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between pr-2 sm:pr-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-3 sm:h-4 w-10 sm:w-12 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>

              {/* Chart area skeleton */}
              <div className="ml-12 sm:ml-16 h-full relative">
                {/* Grid lines */}
                <div className="absolute inset-0 flex flex-col justify-between">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="border-t border-gray-200 w-full" />
                  ))}
                </div>

                {/* Chart bars skeleton */}
                <div className="absolute inset-0 flex items-end justify-between gap-1 px-2">
                  {Array.from({ length: 12 }).map((_, i) => {
                    // Fixed pattern for deterministic rendering
                    const heights = [45, 60, 35, 75, 50, 65, 40, 55, 70, 45, 60, 50];
                    return (
                      <div
                        key={i}
                        className="flex-1 bg-gray-200 rounded-t animate-pulse"
                        style={{
                          height: `${heights[i % heights.length]}%`,
                        }}
                      />
                    );
                  })}
                </div>

                {/* X-axis labels skeleton */}
                <div className="absolute bottom-0 left-0 right-0 flex justify-between px-1">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-2 sm:h-3 w-12 sm:w-16 bg-gray-200 rounded animate-pulse"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Section Skeleton */}
          <div className="space-y-6">
            <div className="flex items-start sm:items-center gap-2">
              <div className="h-4 w-4 bg-gray-200 rounded-full animate-pulse shrink-0 mt-0.5 sm:mt-0" />
              <div className="h-3 sm:h-4 w-full max-w-md sm:max-w-96 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !companyData) {
    return (
      <div className="min-h-screen bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-12">
          <div className="flex items-center justify-center py-12">
            <div className="text-red-500">{error || "Company not found"}</div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate metrics
  const primaryCurrency =
    companyData.revenues.find((r) => r.currency.toLowerCase() === "usd")
      ?.currency ||
    companyData.revenues[0]?.currency ||
    "USD";

  const totalRevenueFormatted = formatCurrency(
    companyData.totalRevenue,
    primaryCurrency
  );

  // Calculate last 30 days revenue
  const thirtyDaysAgo = dayjs().subtract(30, "days").toDate();
  const last30DaysRevenue = companyData.revenues
    .filter((r) => r.type === "REVENUE" && dayjs(r.date).isAfter(thirtyDaysAgo))
    .reduce((sum, r) => sum + r.amount, 0);
  const last30DaysFormatted = formatCurrency(
    last30DaysRevenue,
    primaryCurrency
  );

  // Calculate MRR
  const mrrRevenues = companyData.revenues.filter((r) => r.type === "MRR");
  const latestMRR =
    mrrRevenues.length > 0 ? mrrRevenues[mrrRevenues.length - 1] : null;
  const mrrFormatted = latestMRR
    ? formatCurrency(latestMRR.amount, latestMRR.currency)
    : "-";

  const arrFormatted = latestMRR
    ? formatCurrency(latestMRR.amount * 12, latestMRR.currency)
    : "-";

  // Get founder handle
  const founderHandle = companyData.organization?.users?.[0]?.user?.xHandle;
  const founderDisplay = founderHandle
    ? `@${founderHandle.replace("@", "")}`
    : "";

  // Calculate date filter based on timeFilter
  const getDateFilter = () => {
    switch (timeFilter) {
      case "Last 30 days":
        return dayjs().subtract(30, "days").toDate();
      case "Last 90 days":
        return dayjs().subtract(90, "days").toDate();
      case "Last year":
        return dayjs().subtract(1, "year").toDate();
      default:
        return null; // All time
    }
  };

  const dateFilter = getDateFilter();

  // Prepare chart data from revenues based on filters
  // Always show REVENUE type data
  let filteredRevenues = companyData.revenues.filter(
    (r) => r.type === "REVENUE"
  );

  // Filter by date if needed
  if (dateFilter) {
    filteredRevenues = filteredRevenues.filter((r) =>
      dayjs(r.date).isAfter(dateFilter)
    );
  }

  const revenueData = filteredRevenues
    .map((r) => ({
      date: dayjs(r.date).format("YYYY-MM"),
      value: r.amount / 100, // Convert from cents to dollars
      timestamp: dayjs(r.date).valueOf(),
    }))
    .sort((a, b) => a.timestamp - b.timestamp);

  // Group by month and sum
  const monthlyRevenue = revenueData.reduce((acc, curr) => {
    if (!acc[curr.date]) {
      acc[curr.date] = 0;
    }
    acc[curr.date] += curr.value;
    return acc;
  }, {} as Record<string, number>);

  const chartData = Object.entries(monthlyRevenue)
    .map(([date, value]) => ({ date, value }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const rawMaxValue =
    chartData.length > 0 ? Math.max(...chartData.map((d) => d.value)) : 0;
  // Add 10% padding at the top so the highest point isn't cut off
  const maxValue = rawMaxValue * 1.1;
  const minValue = 0;

  // Format last updated
  const lastUpdated = companyData.lastDateSynced
    ? dayjs(companyData.lastDateSynced).format("MMM D, YYYY, h:mm A")
    : "Never";

  const providerName = companyData?.provider
    ? companyData.provider.charAt(0).toUpperCase() +
      companyData.provider.slice(1)
    : "";

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-12">
        {/* Company Profile */}
        <div className="flex flex-col lg:flex-row items-start justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-start gap-4 sm:gap-6 w-full lg:w-auto">
            {/* Company Logo */}
            {companyData.logoUrl ? (
              <img
                src={companyData.logoUrl}
                alt={companyData.name}
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-orange-400 via-pink-300 to-yellow-300 flex-shrink-0 flex items-center justify-center text-white font-semibold text-base sm:text-lg">
                {getInitials(companyData.name)}
              </div>
            )}

            {/* Company Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">
                  {companyData.name}
                </h1>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="px-2 sm:px-3 py-1 rounded-lg bg-gray-100 text-gray-900 text-xs sm:text-sm font-medium flex items-center gap-1.5">
                    <CheckIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                    Verified
                  </span>
                </div>
              </div>
              <p className="text-gray-500 text-sm sm:text-base mb-2 break-words">
                {companyData.description || "No description available."}
              </p>
              {founderDisplay && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs sm:text-sm">
                    <Link className="hover:font-bold" href={`https://x.com/${founderDisplay.split("@").pop()}`}>
                      {founderDisplay}
                    </Link>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 sm:gap-3 w-full lg:w-auto">
            <Button
              variant="outline"
              className="flex items-center gap-2 flex-1 sm:flex-initial text-sm sm:text-base"
              onClick={handleShare}
            >
              <ShareIcon className="w-4 h-4 shrink-0" />
              {shareCopied ? "Copied!" : "Share"}
            </Button>
            {companyData.websiteUrl && (
              <Button
                variant="outline"
                className="flex items-center gap-2 flex-1 sm:flex-initial text-sm sm:text-base"
                onClick={() => window.open(companyData.websiteUrl, "_blank")}
              >
                <ExternalLinkIcon className="w-4 h-4 shrink-0" />
                Visit
              </Button>
            )}
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 sm:mb-8">
          {/* Total Revenue */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="text-gray-500 text-xs sm:text-sm mb-2">Total revenue</div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 break-words">
              {totalRevenueFormatted}
            </div>
            <div className="text-gray-500 text-xs sm:text-sm">All-time</div>
          </div>

          {/* Last 30 Days */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="text-gray-500 text-xs sm:text-sm mb-2">Last 30 days</div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 break-words">
              {last30DaysFormatted}
            </div>
            <div className="text-gray-500 text-xs sm:text-sm">Recent revenue</div>
          </div>

          {/* MRR */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="text-gray-500 text-xs sm:text-sm mb-2 flex items-center gap-1">
              MRR (estimated)
              <Tooltip
                content={
                  <div>
                    <p>Some subscriptions might show a different</p>
                    <p className="mb-1">number than Stripe dashboard.</p>
                    <p>Revenue is a better metric.</p>
                  </div>
                }
              >
                <InfoIcon className="w-3 h-3 sm:w-4 sm:h-4 cursor-help shrink-0" />
              </Tooltip>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 break-words">
              {mrrFormatted}
            </div>
            <div className="text-gray-500 text-xs sm:text-sm">
              {latestMRR
                ? `${latestMRR.currency.toUpperCase()} MRR`
                : "No MRR data"}
            </div>
          </div>

          {/* Founded */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="text-gray-500 text-xs sm:text-sm mb-2 flex items-center gap-1">
              ARR (estimated)
              <Tooltip
                content={
                  <div>
                    <p>Some subscriptions might show a different</p>
                    <p className="mb-1">number than Stripe dashboard.</p>
                    <p>Revenue is a better metric.</p>
                  </div>
                }
              >
                <InfoIcon className="w-3 h-3 sm:w-4 sm:h-4 cursor-help shrink-0" />
              </Tooltip>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 break-words">
              {arrFormatted}
            </div>
            <div className="text-gray-500 text-xs sm:text-sm">
              {latestMRR
                ? `${latestMRR.currency.toUpperCase()} MRR`
                : "No MRR data"}
            </div>
          </div>
        </div>

        {/* Revenue Graph Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8 overflow-x-auto">
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div className="relative">
              <Dropdown
                options={[
                  "All time",
                  "Last 30 days",
                  "Last 90 days",
                  "Last year",
                ]}
                value={timeFilter}
                onChange={setTimeFilter}
              />
            </div>
          </div>

          {/* Chart */}
          <div className="relative h-64 sm:h-80 md:h-96 min-w-[600px] sm:min-w-0">
            {/* Y-axis labels */}
            {chartData.length > 0 && maxValue > 0 ? (
              <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between pr-2 sm:pr-4">
                {[4, 3, 2, 1, 0].map((i) => {
                  const value = (maxValue / 4) * i;
                  const formatted =
                    value >= 1000000
                      ? `$${(value / 1000000).toFixed(1)}M`
                      : value >= 1000
                      ? `$${(value / 1000).toFixed(0)}k`
                      : `$${value.toFixed(0)}`;
                  return (
                    <span key={i} className="text-gray-500 text-xs sm:text-sm">
                      {formatted}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between pr-2 sm:pr-4">
                <span className="text-gray-500 text-xs sm:text-sm">$0</span>
              </div>
            )}

            {/* Chart area */}
            <div className="ml-12 sm:ml-16 h-full relative">
              {/* Grid lines */}
              <div className="absolute inset-0 flex flex-col justify-between">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="border-t border-gray-200 w-full" />
                ))}
              </div>

              {/* Chart content */}
              {chartData.length > 0 ? (
                <>
                  <svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 800 300"
                    preserveAspectRatio="none"
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {/* Gradient definition */}
                    <defs>
                      <linearGradient
                        id={`chartGradient-${companyData.id}`}
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          stopColor="#3b82f6"
                          stopOpacity="0.3"
                        />
                        <stop
                          offset="100%"
                          stopColor="#3b82f6"
                          stopOpacity="0.05"
                        />
                      </linearGradient>
                    </defs>

                    {/* Area fill */}
                    <path
                      d={`M 0 ${300} ${chartData
                        .map((d, i) => {
                          const divisor =
                            chartData.length > 1 ? chartData.length - 1 : 1;
                          return `${(i / divisor) * 800},${
                            300 -
                            ((d.value - minValue) /
                              (maxValue - minValue || 1)) *
                              300
                          }`;
                        })
                        .join(" ")} L 800 ${300} Z`}
                      fill={`url(#chartGradient-${companyData.id})`}
                    />

                    {/* Line */}
                    <polyline
                      points={chartData
                        .map((d, i) => {
                          const divisor =
                            chartData.length > 1 ? chartData.length - 1 : 1;
                          return `${(i / divisor) * 800},${
                            300 -
                            ((d.value - minValue) /
                              (maxValue - minValue || 1)) *
                              300
                          }`;
                        })
                        .join(" ")}
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {/* Interactive points */}
                    {chartData.map((d, i) => {
                      const divisor =
                        chartData.length > 1 ? chartData.length - 1 : 1;
                      const x = (i / divisor) * 800;
                      const y =
                        300 -
                        ((d.value - minValue) / (maxValue - minValue || 1)) *
                          300;
                      const isHovered = hoveredIndex === i;

                      return (
                        <g key={i}>
                          {/* Invisible larger hit area */}
                          <circle
                            cx={x}
                            cy={y}
                            r="12"
                            fill="transparent"
                            style={{ cursor: "pointer" }}
                            onMouseEnter={() => setHoveredIndex(i)}
                          />
                          {/* Visible point */}
                          <circle
                            cx={x}
                            cy={y}
                            r={isHovered ? "6" : "4"}
                            fill="#3b82f6"
                            stroke="white"
                            strokeWidth="2"
                            style={{ transition: "r 0.2s ease" }}
                          />
                        </g>
                      );
                    })}
                  </svg>

                  {/* Tooltip */}
                  {hoveredIndex !== null && chartData[hoveredIndex] && (
                    <div
                      className="absolute bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm pointer-events-none z-10 whitespace-nowrap"
                      style={{
                        left: `${
                          (hoveredIndex /
                            (chartData.length > 1 ? chartData.length - 1 : 1)) *
                          100
                        }%`,
                        top: `calc(${
                          100 -
                          ((chartData[hoveredIndex].value - minValue) /
                            (maxValue - minValue || 1)) *
                            100
                        }% - 3rem)`,
                        transform: "translateX(-50%)",
                      }}
                    >
                      <div className="font-semibold">
                        {formatCurrency(
                          chartData[hoveredIndex].value * 100,
                          primaryCurrency
                        )}
                      </div>
                      <div className="text-gray-400 text-xs mt-0.5">
                        {formatDate(chartData[hoveredIndex].date)}
                      </div>
                      {/* Tooltip arrow */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                    </div>
                  )}
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                  No revenue data available
                </div>
              )}

              {/* X-axis labels */}
              {chartData.length > 0 && (
                <div className="absolute bottom-0 left-0 right-0 flex justify-between text-gray-500 text-[10px] sm:text-xs mt-2 px-1">
                  {chartData
                    .filter(
                      (_, i) =>
                        i % Math.ceil(chartData.length / 10) === 0 ||
                        i === chartData.length - 1
                    )
                    .map((d) => (
                      <span key={d.date} className="truncate">
                        {dayjs(d.date).format("MMM YYYY")}
                      </span>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="space-y-6">
          {/* Verification Status */}
          <div className="flex items-start sm:items-center gap-2 text-gray-500 text-xs sm:text-sm">
            <CheckIcon className="w-4 h-4 text-green-600 shrink-0 mt-0.5 sm:mt-0" />
            <span className="break-words">
              All revenue is verified through {providerName} API keys. Last
              updated: {lastUpdated}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
